import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { gifts } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Gift, Sparkles, LogIn, Check, UserPlus } from "lucide-react";

// The claim link carries an opaque bearer token. We pass the SAME token
// through to /login and /signup as `?gift=<token>` so the auth page can
// switch to gift-specific messaging AND auto-redeem the gift immediately
// after successful auth.
const buildLoginHref = (token, email) =>
  `/login?redirect=/gift/claim/${token}&email=${encodeURIComponent(email)}&gift=${token}`;
const buildSignupHref = (token, email) =>
  `/signup?redirect=/gift/claim/${token}&email=${encodeURIComponent(email)}&gift=${token}`;

export default function GiftClaimPage() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [err, setErr] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    gifts
      .lookup(token)
      .then(setGift)
      .catch((e) => setErr(e?.response?.data?.detail || "Gift not found"));
  }, [token]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const r = await gifts.redeem(token);
      if (r.already_redeemed) {
        toast.success("Gift already active on your account");
      } else {
        toast.success("Gift activated — welcome to LifeGuide Pro");
      }
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem gift");
      setRedeeming(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center fade-in-up">
          <h1 className="font-display text-2xl text-[#2E2E2E] mb-2">Gift unavailable</h1>
          <p className="text-[#6B6B6B]">{err}</p>
          <Link to="/" className="inline-block mt-6 text-[#4F6F52] font-medium underline-offset-4 hover:underline">
            Go to LifeGuide
          </Link>
        </div>
      </div>
    );
  }

  if (!gift || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">
        Loading...
      </div>
    );
  }

  const emailMismatch =
    user && user.email && user.email.toLowerCase() !== gift.recipient_email.toLowerCase();

  const fromLine = gift.from_display
    ? `${gift.from_display} has given you LifeGuide.`
    : "Someone who cares about you has given you LifeGuide.";
  const months = gift.duration_months;
  const unit = months === 1 ? "month" : "months";
  const durationLabel = `${months} ${unit}`;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md fade-in-up" data-testid="gift-claim-page">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-10 h-10 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
            <Sparkles size={18} className="text-[#F9FAF7]" strokeWidth={1.8} />
          </div>
          <span className="font-display text-2xl text-[#4F6F52]">LifeGuide</span>
        </div>

        <div className="rounded-3xl border border-[#4F6F52]/25 bg-white p-7 shadow-[0_4px_18px_rgba(79,111,82,0.08)]">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={16} className="text-[#4F6F52]" />
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#4F6F52]">
              A gift for {gift.recipient_name}
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#2E2E2E] mb-3 leading-tight">
            {fromLine}
          </h1>
          <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-1">
            You have <strong>{durationLabel}</strong> of LifeGuide Pro
            waiting — unlimited guidance, coach chat, message analyzer, and daily
            devotional audio.
          </p>
          {gift.message && (
            <div className="mt-4 rounded-2xl border-l-4 border-[#4F6F52] bg-[#EEF3EC] px-4 py-3 italic text-[#2E2E2E] text-[14px] leading-relaxed">
              "{gift.message}"
            </div>
          )}

          {gift.already_redeemed ? (
            <div
              className="mt-6 rounded-2xl bg-[#E1ECDD] border border-[#4F6F52]/30 px-4 py-3 flex items-center gap-2 text-[#4F6F52] text-sm"
              data-testid="gift-already-redeemed"
            >
              <Check size={14} /> This gift has already been redeemed.
            </div>
          ) : !user ? (
            <div className="mt-6 space-y-3" data-testid="gift-claim-cta-block">
              {gift.recipient_has_account ? (
                // Existing account path — sign in only.
                <>
                  <p
                    className="text-sm text-[#2E2E2E] leading-relaxed"
                    data-testid="gift-existing-account-msg"
                  >
                    You already have a LifeGuide account. Sign in with{" "}
                    <strong>{gift.recipient_email}</strong> to activate your{" "}
                    <strong>{durationLabel}</strong> gift.
                  </p>
                  <Link
                    to={buildLoginHref(token, gift.recipient_email)}
                    data-testid="gift-login-cta"
                    className="w-full block bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3.5 font-medium transition text-center shadow-[0_4px_14px_rgba(79,111,82,0.18)] inline-flex items-center justify-center gap-2"
                  >
                    <LogIn size={14} /> Sign in to activate
                  </Link>
                </>
              ) : (
                // New-recipient path — create account.
                <>
                  <p
                    className="text-sm text-[#2E2E2E] leading-relaxed"
                    data-testid="gift-new-account-msg"
                  >
                    Create your account with <strong>{gift.recipient_email}</strong>{" "}
                    to activate your <strong>{durationLabel}</strong> of LifeGuide Pro —
                    already paid for as a gift.
                  </p>
                  <Link
                    to={buildSignupHref(token, gift.recipient_email)}
                    data-testid="gift-signup-cta"
                    className="w-full block bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3.5 font-medium transition text-center shadow-[0_4px_14px_rgba(79,111,82,0.18)] inline-flex items-center justify-center gap-2"
                  >
                    <UserPlus size={14} /> Create account & activate gift
                  </Link>
                  <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
                    No credit card. No 3-day trial — this is a paid gift.
                  </p>
                </>
              )}
            </div>
          ) : emailMismatch ? (
            <div
              className="mt-6 rounded-2xl bg-[#F5E6D3]/60 border border-[#A86B4A]/30 px-4 py-3 text-sm text-[#A86B4A]"
              data-testid="gift-email-mismatch"
            >
              This gift is addressed to <strong>{gift.recipient_email}</strong>. Please sign
              in with that email to claim it.
            </div>
          ) : (
            <button
              data-testid="gift-redeem-btn"
              disabled={redeeming}
              onClick={redeem}
              className="mt-6 w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3.5 font-medium transition disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              {redeeming ? "Activating..." : `Activate ${durationLabel} of Pro`}
            </button>
          )}
        </div>

        <p className="text-xs text-[#6B6B6B] text-center mt-6">
          The claim link never expires. Take your time.
        </p>
      </div>
    </div>
  );
}
